"use client";

import { MediaUploadDTO } from "@alba-cars/common-modules";
import clsx from "clsx";
import { AlertCircle, Loader2, RefreshCw, Smartphone, Trash, X } from "lucide-react";
import Image, { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { toast } from "sonner";

import Icon from "@/components/Icons/Icon";
import IconPlus from "@/components/Icons/IconPlus";
import { useWhatsAppWidget } from "@/contexts/WhatsAppWidgetContext";
import { createFolderS3Urls } from "@/lib/api/general/create-s3-url";
import { cn } from "@/lib/utils";
import { compressImageViaApi, CompressionResult } from "@/lib/utils/client-image-compression";
import fileIcon from "@/public/assets/images/file-icon.svg";
import { blurredAlbaCarsLogoDataUrl, DEFAULT_PLACEHOLDER } from "@/utils/constants";

interface ImageUploaderProps<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  maxImages?: number;
  control: Control<T>;
  required?: boolean;
  maxSizeMB?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  labels?: string[]; // Array of labels for each image upload slot
  validateImageCount?: boolean;
  enableCompression?: boolean; // Enable/disable image compression
  compressionQuality?: number; // Compression quality (0-100 for Sharp, 0.0-1.0 for client-side)
  compressionMethod?: "sharp" | "client"; // Choose compression method
  compressionFormat?: "jpeg" | "webp" | "png" | "original"; // Output format for Sharp
  resizeOnCompress?: boolean; // Enable resizing during compression
  minimumCompressionFileSizeKB?: number;
  allowedFileTypes?: Array<"image" | "pdf" | "both">; // Per-slot allowed types
  maxPdfSizeMB?: number; // Optional max size for PDFs
  // Optional per-slot guidelines (opt-in)
  guidelines?: Array<{
    title: string;
    tip: string;
    img: StaticImageData | string;
  }>;
  guidelineOptions?: {
    enabled?: boolean;
    showGhostExample?: boolean;
    showExampleButton?: boolean;
    landscapeBadge?: {
      title: string;
      emphasis: string;
      note: string;
    };
  };
  s3FolderName?: string;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface UploadedImage {
  url: string;
  id: string;
  status: "uploading" | "failed" | "success";
  localUrl?: string;
  file?: File;
  progress?: number;
  error?: string;
  dimensions?: ImageDimensions;
}

type DimensionValidationResult = {
  width: number;
  height: number;
  valid: boolean;
  error?: string;
};

const getPresignedUrl = async (fileType: string, s3FolderName?: string): Promise<MediaUploadDTO> => {
  try {
    const response = await createFolderS3Urls(s3FolderName ?? "bank_valuation_images", [
      { fileName: crypto.randomUUID() + "." + fileType.split("/")[1], fileType: fileType },
    ]);

    if (!response?.length) {
      console.error("Failed to get presigned URL: Empty response");
      throw new Error("Failed to get presigned url");
    }

    return response[0];
  } catch (error) {
    console.error("Error getting presigned URL:", error);
    throw new Error("Failed to get presigned URL: " + (error instanceof Error ? error.message : String(error)));
  }
};

// Utility functions for image validation
const validateImageDimensions = (
  file: File,
  minWidth?: number,
  minHeight?: number,
  maxWidth?: number,
  maxHeight?: number,
): Promise<DimensionValidationResult> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const width = img.width;
      const height = img.height;

      if (minWidth && width < minWidth) {
        resolve({ width, height, valid: false, error: `Image width must be at least ${minWidth}px` });
      }
      if (minHeight && height < minHeight) {
        resolve({ width, height, valid: false, error: `Image height must be at least ${minHeight}px` });
      }
      if (maxWidth && width > maxWidth) {
        resolve({ width, height, valid: false, error: `Image width must not exceed ${maxWidth}px` });
      }
      if (maxHeight && height > maxHeight) {
        resolve({ width, height, valid: false, error: `Image height must not exceed ${maxHeight}px` });
      }

      resolve({ width, height, valid: true });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      console.error("Failed to load image for dimension validation");
      resolve({ width: 0, height: 0, valid: false, error: "Failed to load image" });
    };

    img.src = objectUrl;
  });
};

interface DimensionConstraints {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

// Define a custom type for upload progress tracking
type UploadProgress = {
  [key: number]: number;
};

interface SingleImageUploaderProps {
  buttonId: string;
  image?: UploadedImage;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onRetry: () => void;
  maxSizeMB?: number;
  constraints?: DimensionConstraints;
  disabled?: boolean;
  progressValue?: number; // New prop for real-time progress
  customLabel?: string; // Custom label for this specific image slot
  guideline?: {
    title: string;
    tip: string;
    img: StaticImageData | string;
  };
  showGhostExample?: boolean;
  showExampleButton?: boolean;
  landscapeBadge?: {
    title: string;
    emphasis: string;
    note: string;
  };
  accept?: string;
}

// Enhanced SingleImageUploader component
const SingleImageUploader: React.FC<SingleImageUploaderProps> = ({
  buttonId,
  image,
  onUpload,
  onRemove,
  onRetry,
  maxSizeMB = 5,
  constraints,
  disabled = false,
  progressValue,
  customLabel,
  guideline,
  showGhostExample = false,
  showExampleButton = false,
  landscapeBadge,
  accept = "image/*",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const t = useTranslations("Common.forms.imageUploader");

  const { hideWidget: hideWhatsAppWidget, showWidget: showWhatsAppWidget } = useWhatsAppWidget();

  // Get the actual progress value to display
  const displayProgress = progressValue !== undefined ? progressValue : image?.progress || 0;

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onRemove();
  };

  const handleRetryClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onRetry();

    // Clear the file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // useEffect(() => {
  //   const check = () => setIsMobile(window.innerWidth < 1024);
  //   check();
  //   window.addEventListener("resize", check);
  //   return () => window.removeEventListener("resize", check);
  // }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();

    // Debounce the resize handler to prevent excessive calls
    let timeoutId: number;
    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(check, 150);
    };

    window.addEventListener("resize", debouncedCheck, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedCheck);
    };
  }, []);

  const handleOpenGuide = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setShowGuide(true);
      hideWhatsAppWidget();
    },
    [hideWhatsAppWidget],
  );

  const handleCloseGuide = useCallback(() => {
    setShowGuide(false);
    showWhatsAppWidget();
  }, [showWhatsAppWidget]);

  const toggleGuide = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (showGuide) {
        handleCloseGuide();
      } else {
        handleOpenGuide(e);
      }
    },
    [showGuide, handleCloseGuide, handleOpenGuide],
  );

  // On mobile, if a guideline exists and there's no image yet, clicking the tile opens the help card
  const handleTileClick = useCallback(
    (e: React.MouseEvent<HTMLLabelElement>) => {
      if (disabled) return;
      if (isMobile && guideline && !image?.url && !image?.localUrl) {
        e.preventDefault();
        e.stopPropagation();
        setShowGuide(true);
        hideWhatsAppWidget();
      }
    },
    [disabled, isMobile, guideline, image, hideWhatsAppWidget],
  );

  const handleTileKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLabelElement>) => {
      if (disabled) return;
      if (e.key !== "Enter" && e.key !== " ") return;
      if (isMobile && guideline && !image?.url && !image?.localUrl) {
        e.preventDefault();
        e.stopPropagation();
        setShowGuide(true);
        hideWhatsAppWidget();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    [disabled, isMobile, guideline, image, hideWhatsAppWidget],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleCloseGuide();
      }

      onUpload(e);
    },
    [onUpload, handleCloseGuide],
  );

  // Add escape key listener when guide is open
  useEffect(() => {
    if (!showGuide) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseGuide();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showGuide, handleCloseGuide]);

  useEffect(() => {
    if (isMobile && guideline && showGuide) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile, guideline, showGuide]);

  return (
    <div className="h-full w-full">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
      <label
        htmlFor={`image-upload-${buttonId}`}
        className={clsx("cursor-pointer", disabled && "cursor-not-allowed opacity-60")}
        onClick={handleTileClick}
        onKeyDown={handleTileKeyDown}
        aria-label={
          image?.url || image?.localUrl
            ? "Change uploaded file"
            : isMobile && guideline
              ? accept.includes("pdf")
                ? "Open help and then upload file"
                : "Open help and then upload photo"
              : accept.includes("pdf")
                ? "Upload file"
                : "Upload photo"
        }
      >
        {image?.url || image?.localUrl ? (
          <div className="relative h-full w-full">
            {(() => {
              const isPdf =
                (image?.file && image.file.type === "application/pdf") ||
                (image?.url && image.url.toLowerCase().endsWith(".pdf"));
              if (isPdf) {
                return (
                  <div
                    className={clsx(
                      "flex h-full w-full items-center justify-center rounded-2xl bg-secondary-light",
                      image?.status === "uploading" && "opacity-50",
                    )}
                  >
                    <div className="relative h-16 w-16">
                      <Image src={fileIcon} alt="PDF file" fill />
                    </div>
                  </div>
                );
              }
              return (
                <Image
                  src={image.url && image.url.length >= 1 ? image.url : DEFAULT_PLACEHOLDER}
                  placeholder="blur"
                  blurDataURL={blurredAlbaCarsLogoDataUrl}
                  alt="Uploaded"
                  className={clsx(
                    "h-full w-full rounded-2xl object-cover object-center",
                    image.status === "uploading" && "opacity-50",
                  )}
                  width={400}
                  height={0}
                  quality={50}
                />
              );
            })()}
            {/* Status overlays */}
            {image.status === "uploading" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-black/30">
                <Loader2 className="mb-2 h-8 w-8 animate-spin text-white" />
                <div className="w-3/4">
                  <div className="h-1.5 w-full rounded-full bg-white/30">
                    <div
                      className="h-full rounded-full bg-white transition-all duration-300"
                      style={{ width: `${displayProgress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-center text-sm text-white">{displayProgress}%</p>
                </div>
              </div>
            )}
            {image.status === "failed" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-black/50 p-4 text-center">
                <AlertCircle className="mb-2 h-6 w-6 text-red-400" />
                <p className="mb-2 text-xs text-white">{image.error || "Upload Failed"}</p>
                <button
                  onClick={handleRetryClick}
                  className="flex items-center space-x-1 rounded-lg bg-white px-3 py-1 text-sm hover:bg-gray-100"
                  disabled={disabled}
                >
                  <RefreshCw size={14} />
                  <span>Retry</span>
                </button>
              </div>
            )}
            {image.status === "success" && (
              <>
                <button onClick={handleRemoveClick} className="absolute right-2 top-2" disabled={disabled}>
                  <Icon icon={<Trash size={15} />} backgroundColor="white" className="h-8 w-8" />
                </button>
                {image.dimensions && (
                  <div className="absolute bottom-2 right-2 rounded-lg bg-black/50 px-2 py-1 text-xs text-white">
                    {image.dimensions.width} x {image.dimensions.height}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="relative flex h-full w-full flex-col rounded-2xl bg-secondary-light py-2">
            {showGhostExample && guideline?.img && (
              <>
                <Image
                  src={guideline.img}
                  alt={`${guideline.title} example`}
                  fill
                  sizes="(max-width: 768px) 100vw, 400px"
                  className="pointer-events-none absolute inset-0 select-none rounded-2xl object-cover object-center opacity-10"
                />
                <div className="pointer-events-none absolute inset-0 rounded-2xl bg-white/0" />
              </>
            )}
            {showExampleButton && guideline && (
              <button
                type="button"
                onClick={toggleGuide}
                className="absolute left-2 top-2 z-10 hidden items-center justify-center rounded-full bg-white/90 px-2 py-2 text-xs font-semibold shadow transition-all duration-300 ease-in-out hover:bg-black hover:text-white focus:outline-none sxs:px-3 lg:flex lg:px-4"
                aria-haspopup="dialog"
                aria-expanded={showGuide}
                aria-label={`See example for ${guideline.title}`}
              >
                {showGuide && !isMobile ? t("close") : t("help")}
              </button>
            )}
            <div className="mt-6 flex h-10 items-center justify-center xl:h-16">
              <IconPlus />
            </div>
            <div className="px-1">
              <span className="flex h-8 items-center justify-center text-center font-inter text-xs font-medium text-black xl:h-8 xl:px-20">
                {customLabel || "Upload"}
              </span>
              <span
                className={cn(
                  "mt-2 block text-center text-xs text-gray-500",
                  showExampleButton && "font-semibold text-gray-600",
                )}
              >
                {t("max")} {maxSizeMB}MB
                {constraints && (
                  <>
                    <br />
                    {constraints.minWidth &&
                      constraints.minHeight &&
                      `${constraints.minWidth}x${constraints.minHeight}px`}
                  </>
                )}
              </span>
            </div>

            {showGuide && guideline && (
              <>
                {isMobile ? (
                  <div
                    className="fixed inset-0 z-30 animate-fadeIn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCloseGuide();
                    }}
                    onKeyDown={(e) => e.key === "Escape" && handleCloseGuide()}
                    role="button"
                    tabIndex={0}
                    aria-label="Close example by clicking outside"
                  >
                    <div
                      className="absolute inset-0 animate-fadeIn bg-black/40"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCloseGuide();
                      }}
                      onKeyDown={(e) => e.key === "Escape" && handleCloseGuide()}
                      role="button"
                      tabIndex={-1}
                      aria-label="Close example backdrop"
                    />
                    <div
                      className="inter absolute inset-x-0 bottom-0 animate-slide-up-from-bottom rounded-t-2xl border bg-white p-4 shadow-2xl"
                      style={{ maxHeight: "80svh" }}
                      role="dialog"
                      aria-modal="true"
                    >
                      <div
                        className="h-full w-full"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.key === "Escape" && handleCloseGuide()}
                        role="presentation"
                      >
                        {/* <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-300" /> */}
                        <div className="flex items-center justify-between px-1 py-2">
                          <h4 className="urbanist text-center text-xl font-medium">{guideline.title}</h4>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCloseGuide();
                            }}
                            className="rounded-md px-3 py-2 text-base transition-all duration-300 ease-in-out hover:bg-gray-50 hover:text-black"
                            aria-label="Close example"
                          >
                            <X size={20} />
                          </button>
                        </div>
                        <div className="mt-3 flex flex-col items-center">
                          {landscapeBadge && !accept.includes("pdf") && (
                            <div className="mb-2 w-full rounded-2xl border border-red-400/80 bg-red-50 px-3 py-2">
                              <div className="flex items-center gap-2">
                                {/* <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-red-500 shadow-sm">
                                  <Camera size={16} />
                                </div> */}
                                <div className="min-w-0">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                    {landscapeBadge.title}
                                  </p>
                                  <p className="text-sm font-bold uppercase tracking-wide text-red-600">
                                    {landscapeBadge.emphasis}
                                  </p>
                                </div>
                                <div className="ms-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white text-red-500">
                                  <Smartphone size={24} className="animate-phone-landscape-tilt" />
                                </div>
                              </div>
                              <p className="mt-2 text-sm font-medium text-gray-600">{landscapeBadge.note}</p>
                            </div>
                          )}
                          <Image
                            src={guideline.img}
                            alt={`${guideline.title} example`}
                            className="h-72 w-full rounded-lg object-cover object-top"
                            width={640}
                            height={360}
                            unoptimized
                            sizes="640px"
                            loading="lazy"
                          />
                          <p className="text-muted-foreground mb-2 mt-4 text-base">{guideline.tip}</p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (fileInputRef.current) {
                                fileInputRef.current.click();
                              }
                            }}
                            className="mt-3 w-11/12 rounded-md bg-black px-4 py-3 text-center text-base font-semibold text-white transition-colors duration-200 ease-in-out hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                            aria-label={accept.includes("pdf") ? "Upload file" : "Upload photo"}
                          >
                            {accept.includes("pdf") ? t("uploadFile") : t("uploadImage")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="inter absolute left-2 top-10 z-30 !w-80 animate-fade-in-scale rounded-xl border bg-white p-3 shadow-2xl xxxl:!w-96"
                    role="dialog"
                    aria-label={`${guideline.title} example`}
                    aria-modal="true"
                  >
                    <Image
                      src={guideline.img}
                      alt={`${guideline.title} example`}
                      className={cn(
                        "h-44 w-full rounded-lg object-cover",
                        guideline.title.toLowerCase().includes("rta") && "object-top",
                      )}
                      width={320}
                      height={180}
                      unoptimized
                    />
                    <h5 className="urbanist mt-4 text-center text-lg font-medium">{guideline.title}</h5>
                    <p className="mt-2 text-center text-sm text-secondary-foreground">{guideline.tip}</p>
                    <div className="mt-4 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCloseGuide();
                        }}
                        className="rounded-md bg-gray-100 px-3 py-2 text-xs transition-all duration-300 ease-in-out hover:bg-gray-300 hover:text-black"
                        aria-label="Close example"
                      >
                        {t("close")}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </label>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: "none" }}
        id={`image-upload-${buttonId}`}
        disabled={disabled}
      />
    </div>
  );
};

// Enhanced ImageUploader component with fixes for progress tracking
function ImageUploader<T extends FieldValues>({
  name,
  control,
  required = false,
  label,
  maxImages = 6,
  maxSizeMB = 5,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  labels,
  validateImageCount = false,
  enableCompression = false,
  compressionQuality = 80,
  compressionFormat = "original",
  minimumCompressionFileSizeKB = 300,
  allowedFileTypes,
  maxPdfSizeMB,
  guidelines,
  guidelineOptions,
  s3FolderName,
}: ImageUploaderProps<T>): React.ReactElement {
  // State for real-time progress tracking - separate from form values
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});

  // Track ongoing uploads with a Map of index -> XHR for cancelation capability
  const [uploadXHRs, setUploadXHRs] = useState<Map<number, XMLHttpRequest>>(new Map());

  // Use useRef to safely access the current XHRs map during async operations
  const uploadXHRsRef = useRef<Map<number, XMLHttpRequest>>(new Map());

  // Update the ref whenever the state changes
  useEffect(() => {
    uploadXHRsRef.current = uploadXHRs;
  }, [uploadXHRs]);

  const COMPRESSION_BASE = 30; // % weight allocated to compression phase

  const [compressionIntervals, setCompressionIntervals] = useState<Map<number, number>>(new Map());
  const compressionIntervalsRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    compressionIntervalsRef.current = compressionIntervals;
  }, [compressionIntervals]);

  // Track indices currently in compression to avoid UI flicker between phases
  const [compressingIndices, setCompressingIndices] = useState<Set<number>>(new Set());
  const compressingIndicesRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    compressingIndicesRef.current = compressingIndices;
  }, [compressingIndices]);

  const markCompressingStart = (index: number) => {
    const next = new Set(compressingIndicesRef.current);
    next.add(index);
    compressingIndicesRef.current = next;
    setCompressingIndices(next);
  };

  const markCompressingEnd = (index: number) => {
    if (!compressingIndicesRef.current.has(index)) return;
    const next = new Set(compressingIndicesRef.current);
    next.delete(index);
    compressingIndicesRef.current = next;
    setCompressingIndices(next);
  };

  const startCompressionProgress = (index: number, maxBase = COMPRESSION_BASE) => {
    // mark compressing to keep UI disabled across phase switch
    markCompressingStart(index);
    const intervalId = window.setInterval(() => {
      setUploadProgress((prev) => {
        const current = prev[index] || 0;
        if (current >= maxBase - 1) return prev;

        return { ...prev, [index]: Math.min(maxBase - 1, current + 1) };
      });
    }, 120);

    compressionIntervalsRef.current.set(index, intervalId);
    setCompressionIntervals(new Map(compressionIntervalsRef.current));
  };

  const stopCompressionProgress = (index: number, finalBase = COMPRESSION_BASE) => {
    const id = compressionIntervalsRef.current.get(index);
    if (id !== undefined) {
      window.clearInterval(id);
      compressionIntervalsRef.current.delete(index);
      setCompressionIntervals(new Map(compressionIntervalsRef.current));
    }

    setUploadProgress((prev) => ({ ...prev, [index]: Math.max(prev[index] || 0, finalBase) }));
    // Do NOT clear compressing flag here to avoid flicker; it will be cleared once upload starts or on error
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? "This field is required" : false,
        validate: (value: UploadedImage[] | undefined) => {
          const images = value || [];

          // Filter out undefined values
          const validImages = images.filter((img) => img !== undefined);

          if (validateImageCount && validImages.length < maxImages) {
            toast.error(`Please upload all ${maxImages} images`, {
              duration: 4000,
              className: "shadow-3xl text-center p-6",
              position: "top-center",
            });
            return false;
          }

          if (required && (!validImages.length || !validImages.some((img) => img?.status === "success"))) {
            return "Please upload at least one image successfully";
          }
          if (validImages.some((img) => img?.status === "uploading")) {
            return "Please wait for all images to finish uploading";
          }
          return true;
        },
      }}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        // Ensure value is always an array of UploadedImage | undefined
        const safeValue: (UploadedImage | undefined)[] = Array.isArray(value) ? value : [];

        const slotTypeAt = (index: number): "image" | "pdf" | "both" => {
          return allowedFileTypes?.[index] ?? "image";
        };

        const getAcceptForSlot = (slotType: "image" | "pdf" | "both"): string => {
          if (slotType === "image") return "image/*";
          if (slotType === "pdf") return ".pdf,application/pdf";
          if (slotType === "both") return "image/*,.pdf,application/pdf";
          return "image/*,.pdf,application/pdf";
        };

        const handleImageUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
          const files = e.target.files;
          let file = files?.[0];
          if (!file) return;

          const slotType = slotTypeAt(index);
          const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

          // Enforce per-slot file types
          if (isPdf && slotType === "image") {
            toast.error("This slot only accepts images", {
              duration: 4000,
              className: "shadow-3xl text-center p-6",
              position: "top-center",
            });
            if (e.target) e.target.value = "";
            return;
          }
          if (!isPdf && slotType === "pdf") {
            toast.error("This slot only accepts PDFs", {
              duration: 4000,
              className: "shadow-3xl text-center p-6",
              position: "top-center",
            });
            if (e.target) e.target.value = "";
            return;
          }

          // Cancel any existing upload at this index
          if (uploadXHRsRef.current.has(index)) {
            const xhr = uploadXHRsRef.current.get(index);
            if (xhr) {
              xhr.abort();
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
          }

          // Generate a unique ID for this upload
          const uniqueId = crypto.randomUUID();

          // Reset progress for this index
          setUploadProgress((prev) => ({
            ...prev,
            [index]: 0,
          }));

          // Create local preview and set uploading state immediately so progress is visible during compression
          const localUrl = URL.createObjectURL(file);
          console.log(`Created local preview URL: ${localUrl}`);

          const initialImages = [...safeValue];
          initialImages[index] = {
            url: "",
            id: uniqueId,
            status: "uploading",
            localUrl,
            file,
            progress: 0,
          };
          onChange(initialImages);

          // Validate dimensions for images only
          let dimensionCheck: DimensionValidationResult | undefined;
          if (!isPdf) {
            console.log("Validating image dimensions...");
            dimensionCheck = await validateImageDimensions(file, minWidth, minHeight, maxWidth, maxHeight);
            console.log("Dimension validation result:", dimensionCheck);

            if (!dimensionCheck.valid) {
              console.log(`Dimension validation failed: ${dimensionCheck.error}`);

              const dimErrorImages = [...safeValue];
              dimErrorImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: dimensionCheck.error || "Image dimensions not valid",
                file,
              };
              onChange(dimErrorImages);
              return;
            }
          }

          // Perform compression (and show faux progress) if enabled
          const shouldCompress = !isPdf && enableCompression && file.size > minimumCompressionFileSizeKB * 1024;
          if (shouldCompress) {
            let result: CompressionResult | undefined;

            try {
              startCompressionProgress(index, COMPRESSION_BASE);

              result = await compressImageViaApi(file, {
                quality: compressionQuality,
                format: compressionFormat,
              });

              stopCompressionProgress(index, COMPRESSION_BASE);

              if (result) {
                file = result.file;
              }
            } catch (error) {
              console.error("Error compressing image:", error);

              stopCompressionProgress(index, 0);
              // clear compressing state on error
              markCompressingEnd(index);
            }
          }

          // Validate file size AFTER compression (if any)
          const sizeLimitMB = isPdf ? (typeof maxPdfSizeMB === "number" ? maxPdfSizeMB : maxSizeMB) : maxSizeMB;
          if (file.size > sizeLimitMB * 1024 * 1024) {
            toast.error(`Please upload a file smaller than ${sizeLimitMB}MB`, {
              duration: 4000,
              className: "shadow-3xl text-center p-6",
              position: "top-center",
            });

            if (e.target) {
              e.target.value = "";
            }
            return;
          }

          try {
            // Get presigned URL for S3 upload
            const { uploadUrl, mediaKey: imageId } = await getPresignedUrl(file.type, s3FolderName);

            // Create and configure XHR for upload
            const xhr = new XMLHttpRequest();

            // Store XHR reference to enable cancellation
            uploadXHRsRef.current.set(index, xhr);
            setUploadXHRs(new Map(uploadXHRsRef.current));
            // Now that upload is starting, clear compressing flag to keep busy state continuous without gaps
            markCompressingEnd(index);

            // CRITICAL: Set up progress tracking correctly using our separate state
            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);

                //* Combine compression base with upload progress
                const base = shouldCompress ? COMPRESSION_BASE : 0;
                const combinedProgress = Math.min(100, base + Math.round(((100 - base) * progress) / 100));

                // Update our separate progress state first - this will update the UI immediately
                setUploadProgress((prev) => ({
                  ...prev,
                  [index]: Math.max(prev[index] || 0, combinedProgress),
                }));

                // Only update the form state occasionally to reduce re-renders
                if (progress % 20 === 0 || progress === 100) {
                  const formImages = [...safeValue];
                  if (formImages[index]?.id === uniqueId) {
                    formImages[index] = {
                      ...formImages[index]!,
                      progress: combinedProgress,
                    };
                    onChange(formImages);
                  }
                }
              }
            });

            // Setup completion handler
            xhr.addEventListener("load", () => {
              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              if (shouldCompress) {
                stopCompressionProgress(index, COMPRESSION_BASE);
              }

              // Ensure progress is at 100% in our separate state
              setUploadProgress((prev) => ({
                ...prev,
                [index]: 100,
              }));

              if (xhr.status >= 200 && xhr.status < 300) {
                // Extract clean URL from presigned URL (remove query parameters)
                const url = uploadUrl.split("?")[0];

                // Set progress to 100% in form state
                const formImages = [...safeValue];

                // Update the form state with the success status
                formImages[index] = {
                  url,
                  id: imageId,
                  status: "success",
                  localUrl,
                  dimensions:
                    !isPdf && dimensionCheck
                      ? { width: dimensionCheck.width, height: dimensionCheck.height }
                      : undefined,
                  file,
                  progress: 100,
                };

                onChange(formImages);
              } else {
                console.error(`Upload failed with HTTP status: ${xhr.status}`);

                // Update form state with error
                const formImages = [...safeValue];
                formImages[index] = {
                  url: "",
                  id: uniqueId,
                  status: "failed",
                  localUrl,
                  error: `Upload failed with status code: ${xhr.status}`,
                  file,
                };

                onChange(formImages);
              }
            });

            // Add timeout handler
            xhr.addEventListener("timeout", () => {
              console.error("Upload timed out");

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              if (shouldCompress) {
                stopCompressionProgress(index, 0);
              }
              // Also ensure compressing flag cleared on timeout
              markCompressingEnd(index);

              // Update with error state
              const formImages = [...safeValue];
              formImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: "Upload timed out",
                file,
              };

              onChange(formImages);
            });

            // Error handler
            xhr.addEventListener("error", (e) => {
              console.error("XHR network error:", e);
              toast.error(
                `Upload failed. ERROR: ${JSON.stringify(
                  {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    readyState: xhr.readyState,
                    responseText: xhr.responseText,
                  },
                  null,
                  2,
                )}`,
                {
                  duration: 8000,
                  className: "shadow-3xl text-center p-6",
                  position: "top-center",
                },
              );

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              if (shouldCompress) {
                stopCompressionProgress(index, 0);
              }
              // Also ensure compressing flag cleared on error
              markCompressingEnd(index);

              // Update with error state
              const formImages = [...safeValue];
              formImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: "Network error during upload",
                file,
              };

              onChange(formImages);
            });

            // Abort handler
            xhr.addEventListener("abort", () => {
              console.log("Upload aborted");

              toast.error("Upload aborted", {
                duration: 4000,
                className: "shadow-3xl text-center p-6",
                position: "top-center",
              });

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              if (shouldCompress) {
                stopCompressionProgress(index, 0);
              }
              // Also ensure compressing flag cleared on abort
              markCompressingEnd(index);
            });

            // Configure and send the request
            xhr.open("PUT", uploadUrl);

            // Set a reasonable timeout
            xhr.timeout = 120000; // 2 minutes

            // Set content type to match the file
            xhr.setRequestHeader("Content-Type", file.type);

            xhr.send(file);
          } catch (error) {
            console.error("Upload failed with exception:", error);

            // Update with error state
            const formImages = [...safeValue];
            formImages[index] = {
              url: "",
              id: uniqueId,
              status: "failed",
              localUrl,
              error: error instanceof Error ? error.message : "Upload failed",
              file,
            };

            onChange(formImages);

            // Clean up any XHR reference
            if (uploadXHRsRef.current.has(index)) {
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
            // Ensure compressing flag is cleared if we failed before starting upload
            markCompressingEnd(index);
          }
        };

        const handleRemoveImage = (index: number): void => {
          // Cancel any active upload
          if (uploadXHRsRef.current.has(index)) {
            const xhr = uploadXHRsRef.current.get(index);
            if (xhr) {
              xhr.abort();
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
          }

          //* Stop any compression faux progress
          if (compressionIntervalsRef.current.has(index)) {
            stopCompressionProgress(index, 0);
          }

          // Get the current image to clean up resources
          const currentImage = safeValue[index];

          // Clean up object URL to prevent memory leaks
          if (currentImage?.localUrl) {
            URL.revokeObjectURL(currentImage.localUrl);
          }

          // Create a new array with the value set to undefined
          const newImages = [...safeValue];
          newImages[index] = undefined;

          // Pass the new array directly to onChange
          onChange(newImages);

          // Clear progress for this index
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[index];
            return newProgress;
          });

          // Reset the file input to allow re-uploading the same file
          const fileInput = document.getElementById(`image-upload-${index}`) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }
        };

        const handleRetry = (index: number): (() => Promise<void>) => {
          return async () => {
            // Cancel any active upload
            if (uploadXHRsRef.current.has(index)) {
              return;
            }

            const currentImage = safeValue[index];

            if (!currentImage?.file) {
              return;
            }

            // Use the existing file for retry
            const fakeEvent = {
              target: {
                files: [currentImage.file],
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>;

            await handleImageUpload(index)(fakeEvent);
          };
        };

        // Ensure we have correct amount of placeholders
        const displayItems: (UploadedImage | undefined)[] = [...safeValue];
        while (displayItems.length < maxImages) {
          displayItems.push(undefined);
        }

        // Check if any uploads or compressions are in progress
        const isBusy = uploadXHRs.size > 0 || compressionIntervals.size > 0 || compressingIndices.size > 0;

        // Custom row-based layout configuration with responsive limits
        const getRowLayout = (imageCount: number) => {
          // Dynamic row pattern based on total image count with responsive limits
          const getRowPattern = (count: number) => {
            // Helper function to apply responsive limits
            const applyResponsiveLimits = (pattern: number[]) => {
              // For very small screens (<375px), max 2 per row
              const verySmallPattern = pattern.flatMap((rowCount) => {
                if (rowCount <= 2) return [rowCount];
                const fullRows = Math.floor(rowCount / 2);
                const remainder = rowCount % 2;
                const result = Array(fullRows).fill(2);
                if (remainder > 0) result.push(remainder);
                return result;
              });

              // For small screens (<425px), max 3 per row
              const smallPattern = pattern.flatMap((rowCount) => {
                if (rowCount <= 3) return [rowCount];
                const fullRows = Math.floor(rowCount / 3);
                const remainder = rowCount % 3;
                const result = Array(fullRows).fill(3);
                if (remainder > 0) result.push(remainder);
                return result;
              });

              return {
                base: pattern, // Normal distribution for md+ screens
                small: smallPattern, // Max 3 per row for screens <425px
                verySmall: verySmallPattern, // Max 2 per row for screens <375px
              };
            };

            let basePattern: number[] = [];

            // Your existing pattern logic here...
            if (count <= 1) {
              basePattern = [1];
            } else if (count <= 2) {
              basePattern = [2];
            } else if (count <= 3) {
              basePattern = [3];
            } else if (count <= 4) {
              basePattern = [4];
            } else if (count <= 6) {
              basePattern = count === 5 ? [3, 2] : [3, 3];
            } else if (count <= 8) {
              basePattern = [4, count - 4];
            } else if (count <= 12) {
              if (count === 9) basePattern = [3, 3, 3];
              else if (count === 10) basePattern = [4, 3, 3];
              else if (count === 11) basePattern = [4, 4, 3];
              else if (count === 12) basePattern = [4, 4, 4];
            } else {
              // Your existing large count logic...
              const optimalRowSize = Math.ceil(Math.sqrt(count));
              const rows = [];
              let remaining = count;

              while (remaining > 0) {
                const rowSize = Math.min(optimalRowSize, remaining);
                rows.push(rowSize);
                remaining -= rowSize;
              }

              const totalRows = rows.length;
              const itemsPerRow = Math.ceil(count / totalRows);
              const redistributed = [];
              let remainingItems = count;

              for (let i = 0; i < totalRows; i++) {
                const rowItems = Math.min(itemsPerRow, remainingItems);
                redistributed.push(rowItems);
                remainingItems -= rowItems;
              }

              basePattern = redistributed;
            }

            return applyResponsiveLimits(basePattern);
          };

          const patterns = getRowPattern(imageCount);

          return {
            rowPatterns: patterns,
            gap: imageCount <= 4 ? "gap-4" : imageCount <= 8 ? "gap-3" : "gap-2",
          };
        };

        const layoutConfig = getRowLayout(maxImages);

        // Create different row layouts for different screen sizes
        const createImageRows = (pattern: number[]) => {
          const rows = [];
          let currentIndex = 0;

          for (const rowSize of pattern) {
            rows.push(displayItems.slice(currentIndex, currentIndex + rowSize));
            currentIndex += rowSize;
          }

          return rows;
        };

        // Render function for each screen size
        const renderRowLayout = (rows: (UploadedImage | undefined)[][], className: string = "") => (
          <div className={clsx("space-y-4", className)}>
            {rows.map((row, rowIndex) => (
              <div key={rowIndex} className="w-full">
                <div
                  className={clsx("grid w-full", layoutConfig.gap)}
                  style={{ gridTemplateColumns: `repeat(${row.length}, 1fr)` }}
                >
                  {row.map((img, colIndex) => {
                    const globalIndex = rows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + colIndex;
                    const slotType = slotTypeAt(globalIndex);
                    const accept = getAcceptForSlot(slotType);

                    return (
                      <div key={globalIndex} className="h-36 w-full md:h-40">
                        <SingleImageUploader
                          buttonId={String(globalIndex)}
                          image={img}
                          onUpload={handleImageUpload(globalIndex)}
                          onRemove={() => handleRemoveImage(globalIndex)}
                          onRetry={handleRetry(globalIndex)}
                          maxSizeMB={slotType === "pdf" ? (maxPdfSizeMB ?? maxSizeMB) : maxSizeMB}
                          constraints={{ minWidth, minHeight, maxWidth, maxHeight }}
                          disabled={isBusy}
                          progressValue={uploadProgress[globalIndex]}
                          customLabel={labels?.[globalIndex]}
                          guideline={guidelines?.[globalIndex]}
                          showGhostExample={Boolean(guidelineOptions?.enabled && guidelineOptions?.showGhostExample)}
                          showExampleButton={Boolean(guidelineOptions?.enabled && guidelineOptions?.showExampleButton)}
                          landscapeBadge={guidelineOptions?.landscapeBadge}
                          accept={accept}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );

        return (
          <div className="mt-10">
            {label && <h3 className="font-inter text-base font-bold text-primary-foreground">{label}</h3>}

            {/* Custom Row-Based Layout */}
            <div className="mt-4 w-full">
              {maxImages === 1 ? (
                // Single image - centered
                <div className="flex justify-center">
                  <div className="h-36 w-48">
                    {(() => {
                      const slotType = slotTypeAt(0);
                      const accept = getAcceptForSlot(slotType);
                      return (
                        <SingleImageUploader
                          buttonId="0"
                          image={displayItems[0]}
                          onUpload={handleImageUpload(0)}
                          onRemove={() => handleRemoveImage(0)}
                          onRetry={handleRetry(0)}
                          maxSizeMB={slotType === "pdf" ? (maxPdfSizeMB ?? maxSizeMB) : maxSizeMB}
                          constraints={{ minWidth, minHeight, maxWidth, maxHeight }}
                          disabled={isBusy}
                          progressValue={uploadProgress[0]}
                          customLabel={labels?.[0]}
                          guideline={guidelines?.[0]}
                          showGhostExample={Boolean(guidelineOptions?.enabled && guidelineOptions?.showGhostExample)}
                          showExampleButton={Boolean(guidelineOptions?.enabled && guidelineOptions?.showExampleButton)}
                          landscapeBadge={guidelineOptions?.landscapeBadge}
                          accept={accept}
                        />
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <>
                  {/* Very small screens (<375px) - max 2 per row */}
                  <div className="block xs:hidden">
                    {renderRowLayout(createImageRows(layoutConfig.rowPatterns.verySmall))}
                  </div>
                  {/* Small screens (<425px) - max 3 per row */}
                  <div className="hidden xs:block sm:hidden">
                    {renderRowLayout(createImageRows(layoutConfig.rowPatterns.small))}
                  </div>
                  {/* Medium screens and up */}
                  <div className="hidden sm:block">
                    {renderRowLayout(createImageRows(layoutConfig.rowPatterns.base))}
                  </div>
                </>
              )}
            </div>
            {error && <p className="inter mt-1 text-sm font-semibold text-red-500">{error.message}</p>}
          </div>
        );
      }}
    />
  );
}

export default ImageUploader;
